'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { DefaultValues, FieldValues, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import z from 'zod'

import { RECAPTCHA_ACTIONS } from '@/constants'
import { createContact } from '@/lib/actions/contact.action'
import { getRecaptchaToken } from '@/lib/recaptcha'
import { ContactFormSchema } from '@/lib/validations'

import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'

const ContactForm = () => {
    const defaultValues = {
        name: '',
        email: '',
        phone: '',
        message: '',
    }

    const {
        register,
        reset,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<z.infer<typeof ContactFormSchema>>({
        resolver: zodResolver(ContactFormSchema),
        defaultValues: defaultValues as DefaultValues<FieldValues>,
    })

    const onSubmit = async (data: z.infer<typeof ContactFormSchema>) => {
        const recaptchaToken = await getRecaptchaToken(
            RECAPTCHA_ACTIONS.CONTACT_FORM
        )

        const result = await createContact(recaptchaToken, data)

        if (result?.success) {
            toast.success('Contact Details Submitted Successfully', {
                description: 'Thank you for contacting us!',
            })

            reset()
        } else {
            toast.error(`Error`, {
                description: result?.error?.message,
            })
        }
    }

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            className="font-montserrat pt-10"
        >
            <div className="mx-auto flex flex-col gap-6 w-full">
                <div className="flex flex-col">
                    <label
                        htmlFor="name"
                        className="pb-2 block text-xs font-semibold text-gray-900 dark:text-white uppercase"
                    >
                        Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                        id="name"
                        autoComplete="name"
                        className="bg-transparent text-[#232f5f] flex-1 w-full py-5 px-4 text-xs rounded-none border border-[#232f5f] focus:border focus:outline-none focus:ring-0 focus:border-[#52b3d8] focus-visible:ring-0 focus-visible:border-[#52b3d8] dark:text-[#52b3d8] dark:border-[#52b3d8] dark:focus-visible:border-[#ccc] placeholder:uppercase"
                        placeholder="Name"
                        {...register('name')}
                    />
                    {errors.name && (
                        <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                            {errors.name.message}
                        </p>
                    )}
                </div>
                <div className="flex flex-col">
                    <label
                        htmlFor="email"
                        className="pb-2 block text-xs font-semibold text-gray-900 dark:text-white uppercase"
                    >
                        Email <span className="text-red-500">*</span>
                    </label>
                    <Input
                        id="email"
                        autoComplete="email"
                        className="bg-transparent text-[#232f5f] flex-1 w-full py-5 px-4 text-xs rounded-none border border-[#232f5f] focus:border focus:outline-none focus:ring-0 focus:border-[#52b3d8] focus-visible:ring-0 focus-visible:border-[#52b3d8] dark:text-[#52b3d8] dark:border-[#52b3d8] dark:focus-visible:border-[#ccc] placeholder:uppercase"
                        placeholder="Email"
                        {...register('email')}
                    />
                    {errors.email && (
                        <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                            {errors.email.message}
                        </p>
                    )}
                </div>
                <div className="flex flex-col">
                    <label
                        htmlFor="phone"
                        className="pb-2 block text-xs font-semibold text-gray-900 dark:text-white uppercase"
                    >
                        Phone Number
                    </label>
                    <Input
                        type="text"
                        id="phone"
                        className="bg-transparent text-[#232f5f] flex-1 w-full py-5 px-4 text-xs rounded-none border border-[#232f5f] focus:border focus:outline-none focus:ring-0 focus:border-[#52b3d8] focus-visible:ring-0 focus-visible:border-[#52b3d8] dark:text-[#52b3d8] dark:border-[#52b3d8] dark:focus-visible:border-[#ccc] placeholder:uppercase"
                        placeholder="Phone Number"
                        {...register('phone')}
                    />
                    {errors.phone && (
                        <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                            {errors.phone.message}
                        </p>
                    )}
                </div>
                <div className="flex flex-col">
                    <label
                        htmlFor="message"
                        className="pb-2 block text-xs font-semibold text-gray-900 dark:text-white uppercase"
                    >
                        Message <span className="text-red-500">*</span>
                    </label>
                    <Textarea
                        id="message"
                        rows={4}
                        className="bg-transparent min-h-[120px] text-[#232f5f] flex-1 w-full py-5 px-4 text-xs rounded-none border border-[#232f5f] focus:border focus:outline-none focus:ring-0 focus:border-[#52b3d8] focus-visible:ring-0 focus-visible:border-[#52b3d8] dark:text-[#52b3d8] dark:border-[#52b3d8] dark:focus-visible:border-[#ccc] placeholder:uppercase"
                        defaultValue={''}
                        placeholder="Message"
                        {...register('message')}
                    />
                    {errors.message && (
                        <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                            {errors.message.message}
                        </p>
                    )}
                </div>
                <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="mt-4 w-full h-auto inline-block py-5 px-9 text-xs md:text-sm uppercase rounded-none cursor-pointer bg-[#52b3d899] border border-[#52b3d8] hover:bg-[#52b3d8] text-[#232f5f] dark:text-white hover:text-[#232f5f]"
                >
                    Join
                </Button>
            </div>
        </form>
    )
}

export default ContactForm
